import {
    Activity,
    AlertTriangle,
    Archive,
    Ban,
    Banknote,
    BarChart3,
    BellRing,
    Boxes,
    CalendarClock,
    CalendarDays,
    CircleDollarSign,
    ClipboardList,
    Clock,
    Cog,
    CreditCard,
    Database,
    FileUp,
    FolderTree,
    GalleryHorizontalEnd,
    Gift,
    Layers,
    LayoutDashboard,
    Link2,
    List,
    Mail,
    Network,
    Package,
    PanelLeft,
    PlayCircle,
    Receipt,
    Search,
    ShoppingBag,
    ShoppingCart,
    SlidersHorizontal,
    Sparkles,
    Star,
    Store,
    Tag,
    Tags,
    Ticket,
    Truck,
    Undo2,
    Users,
    WalletCards,
} from 'lucide-react';
import type { ComponentType } from 'react';

import { CartonIcon } from '../components/CartonIcon';

export type NavBadge = 'WIP' | 'BETA' | 'ALPHA' | 'LEGACY';

// Icons accept a `className` prop — wide enough to fit both Lucide icons
// (which spread arbitrary SVG props) and our custom `CartonIcon` (which is a
// plain `<img>` wrapper). a11y is handled by the wrapping `<span>` in the
// Sidebar rather than by the icon component, so any component matching this
// shape can be used.
export type NavIconComponent = ComponentType<{ className?: string }>;

export type NavItem = {
    to: string;
    label: string;
    icon: NavIconComponent;
    badge?: NavBadge;
    // When true, this item is exposed in the installed mobile PWA (see
    // useMobileApp). Items without the flag are hidden from the mobile menu AND
    // their routes are unreachable in mobile mode — MobileGuard redirects them
    // to MOBILE_HOME. This single flag is the source of truth for both.
    mobile?: boolean;
    // Hidden from the shared shop-kiosk role (role.name === 'Shop'). A whole
    // group can set this too — see navGroupsForRole.
    hideForShop?: boolean;
    // Hidden from the Staff role (level 2, role.name === 'Staff'). Same
    // group/item mechanism as hideForShop — see navGroupsForRole.
    hideForStaff?: boolean;
    // Visible only to admins (level >= 3) — hides the item from managers too,
    // unlike hideForShop/hideForStaff which are per-role carve-outs.
    adminOnly?: boolean;
    // Destination lives outside the v2 router subtree (a legacy /v1 route). The
    // Sidebar renders it as a full-page <a href> so the legacy shell boots
    // cleanly, mirroring the v1→v2 "Versiunea noua" link.
    external?: boolean;
};

export type NavGroup = {
    id: string;
    label: string;
    icon: NavIconComponent;
    items: NavItem[];
    hideForShop?: boolean;
    hideForStaff?: boolean;
    // When true, the group renders as a single top-level link (its first item,
    // shown directly with no collapsible header) instead of a toggle + nested
    // list. Used for standalone destinations like the dashboard. The item is
    // kept in `items` so the mobile-subset / MOBILE_HOME derivations still see it.
    flat?: boolean;
};

export const navGroups: NavGroup[] = [
    {
        id: 'home',
        label: 'Dashboard',
        icon: LayoutDashboard,
        flat: true,
        items: [
            { to: '/v2/dashboard', label: 'Dashboard', icon: LayoutDashboard, mobile: true },
        ],
    },
    {
        // Catalog + pricing. Pricing items stay hideForShop (they were a
        // hideForShop group before the merge), so the shop kiosk still only
        // sees Produse + Etichete here.
        id: 'catalog',
        label: 'Catalog',
        icon: Package,
        items: [
            { to: '/v2/products', label: 'Produse', icon: Package, badge: 'BETA' },
            { to: '/v2/products/import', label: 'Import produse', icon: FileUp, badge: 'BETA', hideForShop: true },
            { to: '/v2/tags', label: 'Etichete produse', icon: Tag, badge: 'BETA' },
            { to: '/v2/price-lists', label: 'Liste de preturi', icon: CircleDollarSign, badge: 'BETA', hideForShop: true },
            { to: '/v2/preview', label: 'Preview ca audienta', icon: Sparkles, badge: 'BETA', hideForShop: true },
            { to: '/v2/price-anomalies', label: 'Anomalii de pret', icon: AlertTriangle, badge: 'BETA', hideForShop: true },
        ],
    },
    {
        // No longer flat: Facturi joined Comenzi, so the group renders as a
        // toggle with both entries.
        id: 'orders',
        label: 'Comenzi',
        icon: ShoppingBag,
        items: [
            { to: '/v2/orders', label: 'Comenzi', icon: ShoppingBag, badge: 'ALPHA' },
            { to: '/v2/invoices', label: 'Facturi', icon: Receipt, badge: 'BETA' },
            { to: '/v2/withdrawals', label: 'Retrageri din contract', icon: Undo2, badge: 'BETA' },
        ],
    },
    {
        id: 'singles',
        label: 'Singles',
        icon: CartonIcon,
        items: [
            { to: '/v2/singles/control-panel', label: 'SCP', icon: SlidersHorizontal, badge: 'ALPHA' },
            { to: '/v2/singles/singles-wizard', label: 'Asistent Singles', icon: CartonIcon, badge: 'BETA' },
            { to: '/v2/singles/label-maker', label: 'Generator de etichete', icon: Tags },
            { to: '/v2/singles/cards', label: 'Cauta carti', icon: Search, badge: 'LEGACY' },
            { to: '/v2/singles/singles-expansions', label: 'Seturi singles', icon: Layers, badge: 'LEGACY' },
            { to: '/v2/singles/singles-games', label: 'TCG-uri', icon: WalletCards, badge: 'LEGACY' },
            { to: '/v2/singles/operations', label: 'Operatii singles', icon: PlayCircle, badge: 'LEGACY' },
        ],
    },
    {
        id: 'customers',
        label: 'Clienti',
        icon: Users,
        items: [
            { to: '/v2/users', label: 'Utilizatori', icon: Users, badge: 'BETA' },
            { to: '/v2/user-tags', label: 'Tag-uri utilizatori', icon: Tag, badge: 'BETA', hideForShop: true },
            { to: '/v2/order-flags', label: 'Lista neagra livrare', icon: Ban, badge: 'BETA' },
            { to: '/v2/giveaways', label: 'Giveaway', icon: Gift, badge: 'BETA', hideForShop: true },
        ],
    },
    {
        // Kept as its own group (not folded into Clienti): the newsletter module
        // is slated to live here alongside Scurtare URL.
        id: 'marketing',
        label: 'Marketing',
        icon: Link2,
        hideForShop: true,
        items: [
            { to: '/v2/short-urls', label: 'Scurtare URL', icon: Link2, badge: 'BETA' },
        ],
    },
    {
        // Shop operations + team (HR). The HR items keep hideForShop (they were
        // the hideForShop "Echipa" group), so the shop kiosk sees only the
        // POS / calendar / tickets / reports / push operations here.
        id: 'shop-ops',
        label: 'Magazin',
        icon: Store,
        items: [
            { to: '/v2/pos', label: 'POS / Casa de marcat', icon: CircleDollarSign, badge: 'BETA' },
            // No static badge — the Sidebar renders a live my-open-tickets count here.
            { to: '/v2/tickets', label: 'Tickete', icon: ClipboardList, mobile: true },
            { to: '/v2/day-reports', label: 'Rapoarte zilnice', icon: Banknote, mobile: true },
            { to: '/shipment-manifests', label: 'Borderouri livrari', icon: Truck, external: true },
            { to: '/v2/push', label: 'Notificari push', icon: BellRing, badge: 'BETA' },
            { to: '/v2/hr/vacations', label: 'Concedii', icon: CalendarDays, hideForShop: true },
            { to: '/v2/hr/shops', label: 'Magazine', icon: Store, hideForShop: true },
        ],
    },
    {
        // Manager + Admin only — Staff and Shop see nothing (data is scoped
        // server-side; managers get just their store).
        id: 'reports',
        label: 'Rapoarte',
        icon: BarChart3,
        hideForShop: true,
        hideForStaff: true,
        items: [
            { to: '/v2/reports/general', label: 'Raport general', icon: BarChart3, badge: 'BETA' },
            { to: '/v2/reports/pos', label: 'POS pe ore', icon: Clock, badge: 'BETA' },
            { to: '/v2/reports/events', label: 'Raport evenimente', icon: CalendarDays, badge: 'BETA' },
            { to: '/v2/reports/customers', label: 'Clienti noi vs recurenti', icon: Users, badge: 'BETA', adminOnly: true },
        ],
    },
    {
        id: 'events',
        label: 'Evenimente',
        icon: CalendarDays,
        items: [
            { to: '/v2/calendar', label: 'Calendar evenimente', icon: CalendarDays, badge: 'BETA' },
            { to: '/v2/current-events', label: 'Evenimente curente', icon: CalendarClock, badge: 'BETA' },
            { to: '/v2/reservations', label: 'Rezervari', icon: Ticket, badge: 'BETA' },
        ],
    },
    {
        // System + analytics.
        id: 'system',
        label: 'Sistem',
        icon: Cog,
        hideForShop: true,
        items: [
            { to: '/v2/analytics', label: 'Analitice functii', icon: Activity, badge: 'BETA' },
            { to: '/v2/jobs', label: 'Joburi programate', icon: Clock, badge: 'WIP' },
            { to: '/v2/shipping-debug', label: 'Debug shipper', icon: Truck, badge: 'BETA' },
            { to: '/v2/email-debug', label: 'Debug email', icon: Mail, badge: 'BETA' },
        ],
    },
    {
        // The legacy OrchestraMenu items, surfaced as a fallback while features
        // migrate to v2. All `external` (full-page nav into the legacy shell).
        // The old per-admin gating isn't replicated here — menu visibility was
        // never the boundary; the legacy routes enforce their own access.
        id: 'v1',
        label: 'Clasic (v1)',
        icon: Archive,
        hideForShop: true,
        items: [
            { to: '/v1/dashboard', label: 'Dashboard', icon: LayoutDashboard, external: true },
            { to: '/orders', label: 'Comenzi', icon: ShoppingBag, external: true },
            { to: '/carts', label: 'Cosuri', icon: ShoppingCart, external: true },
            { to: '/products', label: 'Produse', icon: Package, external: true },
            { to: '/catalog', label: 'Catalog', icon: Database, external: true },
            { to: '/cards', label: 'TCG Singles', icon: Layers, external: true },
            { to: '/product-lists', label: 'Liste produse', icon: List, external: true },
            { to: '/category-lists', label: 'Liste categorii', icon: List, external: true },
            { to: '/users', label: 'Utilizatori', icon: Users, external: true },
            { to: '/offers', label: 'Oferte', icon: Tag, external: true },
            { to: '/vouchers', label: 'Vouchere', icon: Ticket, external: true },
            { to: '/reports/legacy-reports', label: 'Legacy Reports', icon: BarChart3, external: true },
            { to: '/reports/new-reports', label: 'New Reports', icon: BarChart3, external: true },
            { to: '/reports/gamesworkshop-stockcheck', label: 'GamesWorkshop Stockcheck', icon: BarChart3, external: true },
            { to: '/calendar', label: 'Calendar', icon: CalendarDays, external: true },
            { to: '/carousel', label: 'Carusel', icon: GalleryHorizontalEnd, external: true },
            { to: '/interest-registrations', label: 'Inregistrari interes', icon: Star, external: true },
            { to: '/sidebar', label: 'Sidebar meniu', icon: PanelLeft, external: true },
            { to: '/attributes', label: 'Atribute', icon: Network, external: true },
            { to: '/categories', label: 'Categorii', icon: FolderTree, external: true },
            { to: '/suppliers', label: 'Furnizori', icon: Truck, external: true },
            { to: '/stock-operations', label: 'Operatii stocuri', icon: Truck, external: true },
            { to: '/inventory-buffer', label: 'Inventory Buffer', icon: Boxes, external: true },
            { to: '/payment-types', label: 'Modalitati plata', icon: CreditCard, external: true },
            { to: '/stock-accounts', label: 'Conturi', icon: ClipboardList, external: true },
        ],
    },
];

// Shop kiosk accounts (role.name === 'Shop') get a trimmed menu: the marketing,
// system and v1 groups plus the pricing, user-tags, giveaway and HR items are
// dropped via their hideForShop flags; singles, catalog basics, customer basics
// and the POS / shop-ops operations stay. Menu-only for now — route guards and
// API grants are the real lockdown and are still TODO.
export function navGroupsForRole(
    groups: NavGroup[],
    role: { isShop?: boolean; isStaff?: boolean; isAdmin?: boolean },
): NavGroup[] {
    const { isShop = false, isStaff = false, isAdmin = false } = role;
    if (isAdmin && !isShop && !isStaff) return groups;
    const hideGroup = (g: NavGroup) => (isShop && g.hideForShop) || (isStaff && g.hideForStaff);
    const hideItem = (i: NavItem) =>
        (isShop && i.hideForShop) || (isStaff && i.hideForStaff) || (!isAdmin && i.adminOnly);
    return groups
        .filter((group) => !hideGroup(group))
        .map((group) => ({ ...group, items: group.items.filter((item) => !hideItem(item)) }))
        .filter((group) => group.items.length > 0);
}

// TEMPORARY: the installed PWA currently exposes the full desktop menu and all
// routes while we sort out mobile-specific screens. Flip back to false to
// re-enable the curated mobile subset (mobileNavGroups / isMobileAllowedPath).
export const MOBILE_FULL_ACCESS = true;

// navGroups reduced to only the mobile-enabled items, with now-empty groups
// removed. Used as the curated mobile subset when MOBILE_FULL_ACCESS is off.
export const mobileNavGroups: NavGroup[] = navGroups
    .map((group) => ({ ...group, items: group.items.filter((item) => item.mobile) }))
    .filter((group) => group.items.length > 0);

// Flat list of mobile-enabled destination paths, derived from the same flag.
const mobilePaths = mobileNavGroups.flatMap((group) => group.items.map((item) => item.to));

// Where the installed app lands, and the fallback MobileGuard redirects to when
// a route is off-limits on mobile. Falls back to '/v2' if nothing is flagged.
export const MOBILE_HOME = mobilePaths[0] ?? '/v2';

// A path is allowed in mobile mode if it equals a mobile item's `to` or nests
// under it (e.g. '/v2/day-reports/create' rides along with '/v2/day-reports').
export function isMobileAllowedPath(pathname: string): boolean {
    if (MOBILE_FULL_ACCESS) return true;
    return mobilePaths.some((path) => pathname === path || pathname.startsWith(path + '/'));
}
